<script setup>

import { WarningFilled } from "@element-plus/icons-vue";
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import TrackerProjectInfo from "@/components/shared/TrackerProjectInfo.vue";
import Notification from "@/utils/plugins/NotificationService";
import TrackerService from "@/api/services/tracker.service";
import Clock from "@/components/shared/Clock.vue";
import TrackerSplit from "@/components/shared/TrackerSplit.vue";
import SharedDeleteConfirmDialog from "@/components/shared/SharedDeleteConfirmDialog.vue";
import ProjectsService from "@/api/services/projects.service";
import TimeEntryTagService from "@/api/services/timeEntryTag.service";
import moment from "moment";
import { formatDate } from "@/utils/dateFormat";
import TrackerProjectSearch from "@/components/shared/TrackerProjectSearch.vue";
import { isDate } from "lodash";
import { useAuthStore } from "@/app/stores/authStore";
import { usePresenceStore } from "@/app/stores/presenceStore";
import { useUiStore } from "@/app/stores/uiStore";

const authStore = useAuthStore();
const presenceStore = usePresenceStore();
const uiStore = useUiStore();
const user = computed(() => authStore.getUserLogged);
const page = ref(1);
const itemsPerPage = ref(9999);
const archived = ref(false);

const description = ref('');
const changeDescription = ref(false);
const project = ref();

const PROJECT_API = "/api/projects/";
const TAGS_API = "/api/time_entry_tags/";

const tags = ref();
const optionTags = ref([]);
const optionProjects = ref([]);

const refInputStartTime = ref(null);
const inputStartTime = ref();
const newStartTime = ref();
const refDropdownProject = ref();
const dropdownProject = ref(false);
const disabledStopButton = ref(false);

// Autocomplete Description
const refAutocompleteDescription = ref();
const entriesOptions = ref([]);
const isSelectedEntry = ref(false);

// Límite de caracteres para descripción
const MAX_DESCRIPTION_LENGTH = 255;
const descriptionLength = computed(() => {
  return description.value ? description.value.length : 0;
});

const showCharCounter = computed(() => {
  return descriptionLength.value > 200;
});

const charCounterClass = computed(() => {
  if (descriptionLength.value >= MAX_DESCRIPTION_LENGTH) {
    return 'char-counter-limit';
  } else if (descriptionLength.value >= 240) {
    return 'char-counter-warning';
  } else {
    return 'char-counter-info';
  }
});

const dialogValidation8h = ref(false);
const discardDialogShown = ref(false);
const isAcceptedNomoreThan8 = ref(false);
const validation8hTitle = "Confirmar duración";
const dialogWidth = "420px";
const isMobile = ref(false);

// Titles
const DEFAULT_TITLE = "ConnectUs";
// eslint-disable-next-line no-unused-vars
const trackernetTitle = " ● ConnectUs";

const day = computed(() => {
  const entryStart = new Date(props.entry.timeStart)
  const today = new Date();
  if (isSameDay(today, entryStart)) {
    return 'Hoy'
  } else {
    return moment(props.entry.timeStart).format("DD/MM/YYYY");
  }
})

const daysDiff = computed(() => {
  return getDaysDifference(new Date(props.entry.timeStart), new Date());
})

// eslint-disable-next-line no-undef
const emits = defineEmits(['reloadEntries']);

// eslint-disable-next-line no-undef
const props = defineProps({
  entry: Object,
  restarting: { type: Boolean, default: false, }
})

onMounted(() => {
  description.value = props.entry.description;
  project.value = props.entry.project;
  tags.value = props.entry.tags;
  getProjects();
  getTags();
  startTotalTime();
  inputStartTime.value = startTime.value;
})

onUnmounted(() => {
  document.title = DEFAULT_TITLE;
})


const duration = computed(() => presenceStore.entryDuration);

const newDuration = ref();
const setDuration = () => {
  const duration = parseInt(newDuration.value, 10);
  if (!isNaN(duration)) {
    presenceStore.setEntryDuration(duration);
  }
}

const startTime = computed(() => {
  const temp = moment(props.entry.timeStart).format('HH:mm')
  return temp;
})

const endTime = computed(() => {
  const newDate = new Date(props.entry.timeStart);
  newDate.setHours(newDate.getHours() + (totalTime.value.hours || 0));
  newDate.setMinutes(newDate.getMinutes() + (totalTime.value.minutes || 0));
  newDate.setSeconds(newDate.getSeconds() + (totalTime.value.seconds || 0));

  const temp = moment(newDate).format('HH:mm')

  return temp;
})

const totalTime = ref({
  hours: 0,
  minutes: 0,
  seconds: 0,
});

const startTotalTime = () => {
  totalTime.value.hours = props.entry.totalTime.hours;
  totalTime.value.minutes = props.entry.totalTime.minutes;
  totalTime.value.seconds = props.entry.totalTime.seconds;
}

watch(duration, (newVal) => {
  const hours = Math.floor(newVal / 3600);
  const minutes = Math.floor((newVal % 3600) / 60); // Resto entre 3600 y divide entre 60 para minutos
  const seconds = newVal % 60; // Resto final para segundos
  totalTime.value = {
    hours,
    minutes,
    seconds
  }
  if (newVal > 0) {
    const h = String(hours).padStart(2, '0');
    const m = String(minutes).padStart(2, '0');
    const s = String(seconds).padStart(2, '0');
    document.title = `${h}:${m}:${s} ` + trackernetTitle;
  } else {
    document.title = DEFAULT_TITLE;
  }
})

const dialogSplit = ref(false);


// eslint-disable-next-line no-unused-vars
const entryDuration = computed(() => presenceStore.entryDuration);
// eslint-disable-next-line no-unused-vars
const startInterval = () => presenceStore.startInterval();
// eslint-disable-next-line no-unused-vars
const stopInterval = () => presenceStore.stopInterval();

const entry = computed(() => {
  return props.entry;
})

const getProjects = () => {
  let params = {
    page: page.value,
    itemsPerPage: itemsPerPage.value,
    archived: archived.value,
    'order[initialDate]': 'DESC'
  }
  uiStore.startLoading();
  ProjectsService.getProjects(params)
    .then((response) => {
      optionProjects.value = response.data['hydra:member'];
    })
    .catch((error) => {
      console.log(error);
      Notification.addNotification(
        "Error al cargar los proyectos"
      );
      uiStore.stopLoading();
    })
    .finally(() => {
      uiStore.stopLoading();
    });
}

const getTags = () => {
  let params = {
    page: page.value,
    itemsPerPage: itemsPerPage.value,
  }
  params['order[name]'] = 'ASC';
  uiStore.startLoading();
  TimeEntryTagService.getTags(params)
    .then((response) => {
      optionTags.value = response.data['hydra:member'];
    })
    .catch((error) => {
      console.log(error);
      Notification.addNotification(
        "Error al cargar las etiquetas"
      );
      uiStore.stopLoading();
    })
    .finally(() => {
      uiStore.stopLoading();
    });

}

const tagsText = computed(() => {
  let temp = "Elegir etiqueta";
  for (let i = 0; i < entry.value.tags.length; i++) {
    const element = entry.value.tags[i];
    if (i == 0) {
      temp = element.name;
    } else {
      temp = temp + ", " + element.name
    }
  }
  return temp;
})


const stopTimeEntry = async () => {
  disabledStopButton.value = true;
  let entry = { ...props.entry };

  if (entry.description == '' || !entry.project || !entry.tags) {
    disabledStopButton.value = false;
    let mssg = "No se ha podido cerrar la tarea, falta: ";
    if (entry.description == '') mssg = mssg + 'descripción '
    if (!entry.project.name) mssg = mssg + 'proyecto '
    if (entry.tags.length < 1) mssg = mssg + 'etiquetas '
    Notification.addNotification({
      message: mssg,
      type: "error",
      duration: 6000
    })
    return;
  }

  // Capturar el momento exacto de cierre al pulsar el botón
  const now = new Date();
  const formattedTimeEnd = formatDate(now);

  entry.project = entry.project['@id'];
  let tags = [];
  entry.tags.forEach((tag) => {
    tags.push(tag['@id']);
  });
  entry.tags = tags;
  entry.timeStart = formatDate(new Date(entry.timeStart))
  entry.timeEnd = formattedTimeEnd;
  entry.employee = entry.employee['@id'];

  if (!isValidDurationMax8h(entry.timeStart, entry.timeEnd)) {
    if (isAcceptedNomoreThan8.value) {
      const m = moment.parseZone(entry.timeStart);
      const t = m.add(8, 'hours').format();
      entry.timeEnd = formatDate(new Date(t));
    } else {
      dialogValidation8h.value = true;
      return;
    }
  }

  // Calcular la duración en segundos basándose en timeEnd - timeStart
  const startTime = moment(entry.timeStart);
  const endTime = moment(entry.timeEnd);
  entry.duration = Math.floor(endTime.diff(startTime) / 1000);

  uiStore.startLoading();
  try {
    await TrackerService.stopEntry(entry);
    stopInterval();
    await reloadEntries();
    disabledStopButton.value = false;
  } catch (error) {
    console.log(error);
    Notification.addNotification(
      "Error al parar la tarea"
    );
  } finally {
    uiStore.stopLoading();
    if (presenceStore.isEntryActive) {
      presenceStore.setIsEntryActive(false);
    }
  }
}

const restarOtherEntry = computed(() => {
  return props.restarting;
})

watch(restarOtherEntry, (newVal) => {
  if (newVal) {
    stopTimeEntry();
  }
})

const discard = () => {
  discardDialogShown.value = true;
}

const confirmDiscard = async () => {
  try {
    const id = props.entry.id;
    await TrackerService.discardEntry(id);
    discardDialogShown.value = false;
    reloadEntries()
    Notification.addNotification("La entrada se ha eliminado", "success", 10000)
  } catch (error) {
    console.log(error);
    Notification.addNotification(
      "Error al eiminar la tarea"
    );
  }
}

const reloadEntries = () => {
  emits('reloadEntries');
}

// Update
const updateEntry = (entry) => {
  let tags = [];

  for (let i = 0; i < entry.tags.length; i++) {
    const tag = entry.tags[i];
    tags.push(TAGS_API + tag.id)
  }
  let params = {
    "employee": entry.employee['@id'],
    "project": PROJECT_API + entry.project.id,
    "tags": tags,
    "description": entry.description,
    "timeStart": entry.timeStart,
    "billable": entry.billable,
  }
  return TrackerService.updateEntry(entry.id, params);
}


// Description
//Se ejecuta cuando se cambia la descripción
const changeEntryDescription = () => {
  changeDescription.value = true;
  checkAndChangeDescription();

}

// Esto comprueba que esten ok los cambios pertinentes, y si estan ok, pues hace el cambio
const checkAndChangeDescription = () => {
  if (!changeDescription.value) {
    return;
  }
  if (isSelectedEntry.value) {
    isSelectedEntry.value = false;
    return;
  }
  if (description.value.length < 5) {
    Notification.addNotification({
      message: "La descripción tiene que contener al menos 5 caracteres",
      type: "error",
      duration: 6000
    })
    if (description.value.length === 0) {
      description.value = props.entry.description;
    }
    return;
  }
  changeDescription.value = false;
  const entry = props.entry;
  entry.description = description.value;
  uiStore.startLoading();
  updateEntry(entry)
    .then(() => {
      Notification.addNotification({
        message: "Se actualizó la tarea correctamente",
        type: "success",
        duration: 6000
      })
      uiStore.stopLoading();
    })
    .catch((error) => {
      console.log(error);
      Notification.addNotification(
        "Error al actualizar la descripción"
      );
      uiStore.stopLoading();
    })
}

// Esto se utiliza para que cuando el autocomplete reciba un cambio en el input, se haga la petición para conseguir los entries
const querySearch = (queryString, callback) => {
  // Filtra las entradas basándose en el queryString (valor actual del input)
  if (queryString.length < 3) {
    callback([]);
    return;
  }
  let params = {
    page: 1,
    itemsPerPage: 9,
    'order[timeStart]': 'DESC',
    description: queryString,
  }

  if (!user.value?.id) {
    callback([]);
    return;
  }

  TrackerService.getEmployeeEntries(user.value.id, params).then((res) => {
    entriesOptions.value = [];
    for (let i = 0; i < res.data['hydra:member'].length; i++) {
      const entry = res.data['hydra:member'][i];
      if (entry.timeEnd) {
        entriesOptions.value.push(entry)
      }
    }
    callback(entriesOptions.value);
  })
};

// Cuando se elige una nueva entrada, hace los cambios y lo actualiza
const handleSelect = (val) => {
  selectNewEntry(val)
}

const moveDescriptionCursorToEdge = (event) => {
  if (event.key !== 'Home' && event.key !== 'End') return;

  const input = event.target;
  if (!(input instanceof HTMLInputElement)) return;

  const position = event.key === 'Home' ? 0 : input.value.length;
  const anchor = input.selectionStart ?? position;
  event.preventDefault();
  event.stopPropagation();
  input.setSelectionRange(event.shiftKey ? anchor : position, position);
}

const selectNewEntry = (entry) => {
  isSelectedEntry.value = true;
  let newEntry = props.entry;
  description.value = entry.description;
  newEntry.description = entry.description;
  // props.entry = entry.billable;
  newEntry.billable = entry.billable;
  project.value = entry.project;
  newEntry.project = entry.project;
  tags.value = entry.tags;
  newEntry.tags = entry.tags;

  updateEntry(newEntry).then(() => {
    Notification.addNotification({
      message: "Se actualizó la tarea correctamente",
      type: "success",
      duration: 6000
    })
    uiStore.stopLoading();
  })
    .catch((error) => {
      console.log(error);
      Notification.addNotification(
        "Error al actualizar la descripción"
      );
      uiStore.stopLoading();
    });
}

// Cambia la descripción del model
// eslint-disable-next-line no-unused-vars
const handleUpdateModel = (val) => {
  description.value = val;
}

const getTagsText = (tags) => {
  let text = '';
  for (let i = 0; i < tags.length; i++) {
    const element = tags[i];
    if (i == 0) {
      text = element.name;
    } else {
      text = text + ", " + element.name
    }
  }
  return text;
}

// Billable
const changeBillable = () => {
  let entry = props.entry;
  entry.billable = !entry.billable;
  updateEntry(entry).then(() => {
    Notification.addNotification({
      message: "Se actualizó la tarea correctamente",
      type: "success",
      duration: 6000
    })
  }).catch((error) => {
    console.log(error);
    Notification.addNotification(
      "Error al cambiar la facturacion"
    );
  });

}

// Project
const changeProject = () => {
  uiStore.startLoading();
  const entry = props.entry;
  entry.project = project.value;
  updateEntry(entry)
    .then(() => {
      Notification.addNotification({
        message: "Se actualizó la tarea correctamente",
        type: "success",
        duration: 6000
      })
      uiStore.stopLoading();
      reloadEntries();
    }).catch((error) => {
      console.log(error);
      Notification.addNotification(
        "Error al cambiar el proyecto"
      );
      uiStore.stopLoading();
    })
}

// Gets the new project selected
const getNewProject = (newProject) => {
  project.value = newProject;
  refDropdownProject.value.handleClose();
  changeProject();
}

const isDropdownOpen = ref(false);

// Handle when the dropdown is closed
const handleDropdownProjectVisibleChange = (visible) => {
  dropdownProject.value = visible;
  isDropdownOpen.value = visible;
}

// Tags
const changeTags = () => {
  uiStore.startLoading();

  if (tags.value.length <= 0) {
    Notification.addNotification({
      message: "Tiene que haber minimo una tarea",
      type: "error",
      duration: 6000
    })
    tags.value = props.entry.tags;
    uiStore.stopLoading();
    return;
  }
  const entry = props.entry;
  entry.tags = tags.value;
  updateEntry(entry)
    .then(() => {
      uiStore.stopLoading();
      Notification.addNotification({
        message: "Se actualizó la tarea correctamente",
        type: "success",
        duration: 6000
      })
      reloadEntries();
    }).catch((error) => {
      console.log(error);
      Notification.addNotification(
        "Error al cambiar las etiquetas"
      );
      uiStore.stopLoading();
    })
}

// Funciones Split
const openDialogSplit = () => {
  dialogSplit.value = true;
}

const closeDialogSplit = () => {
  dialogSplit.value = false;
}

const isDisabledSplit = computed(() => {
  if (totalTime.value.hours <= 0) {
    if (totalTime.value.minutes < 15) {
      return true
    } else {
      return false;
    }
  } else { return false }
})

// change start time

const focusInputHour = () => {
  inputStartTime.value = inputStartTime.value.split(':');
  let temp = inputStartTime.value[0] + inputStartTime.value[1];
  inputStartTime.value = temp;
}

const blurInputHour = () => {
  if (newStartTime.value) {
    inputStartTime.value = newStartTime.value;
  } else {
    inputStartTime.value = startTime.value;
  }
}

const checkChangeInputHour = () => {
  let temp;
  const newDuration = new Date();
  const now = new Date();
  if (inputStartTime.value.length == 3 || inputStartTime.value.length == 4) {
    if (inputStartTime.value.length == 3) temp = inputStartTime.value.slice(0, 1) + ':' + inputStartTime.value.slice(1);
    if (inputStartTime.value.length == 4) temp = inputStartTime.value.slice(0, 2) + ':' + inputStartTime.value.slice(2);
    temp = temp.split(':')

    newDuration.setHours(parseInt(temp[0]))
    newDuration.setMinutes(parseInt(temp[1]))
    newDuration.setSeconds(0)
  }
  if (inputStartTime.value.length == 1 || inputStartTime.value.length == 2) {
    newDuration.setHours(parseInt(inputStartTime.value))
    newDuration.setMinutes(0);
    newDuration.setSeconds(0);
  }

  if (newDuration > now) {
    newDuration.setDate(newDuration.getDate() - 1);
  }

  //Comprobar que el valor entra dentro de los valores
  if (newDuration > now || isNaN(newDuration) || inputStartTime.value.length >= 5) {
    refInputStartTime.value.blur();
    return;
  }

  const hours = newDuration.getHours().toString().padStart(2, "0");
  const minutes = newDuration.getMinutes().toString().padStart(2, "0");
  newStartTime.value = `${hours}:${minutes}`;

  changeInputHour(newDuration);
  refInputStartTime.value.blur();
}

const changeInputHour = (newStart) => {
  uiStore.startLoading();
  const entry = props.entry;
  entry.timeStart = formatDate(newStart);
  const totalTime = calcularTiempoTarea(formatDate(newStart));
  let total = totalTime.hours * 3600;
  total += totalTime.minutes * 60;
  total += totalTime.seconds;
  entry.duration = total;
  newDuration.value = total;
  updateEntry(entry)
    .then(() => {
      Notification.addNotification({
        message: "Se actualizó la tarea correctamente",
        type: "success",
        duration: 6000
      })
    })
    .catch((error) => {
      console.log(error);
      Notification.addNotification(
        "Error al cambiar la hora de inicio"
      );
      uiStore.stopLoading();
    })
    .finally(() => {
      presenceStore.setEntryDuration(newDuration.value);
      uiStore.stopLoading();
      setDuration();
    })
}

const calcularTiempoTarea = (fecha) => {
  let time = new Date(fecha)
  const now = new Date();
  const diferenciaMs = now - time;
  if (diferenciaMs < 0) {
    throw new Error("La fecha proporcionada es  futura, no se puede calcular el tiempo transcurrido.");
  }
  const segundosTotales = Math.floor(diferenciaMs / 1000);
  const hours = Math.floor(segundosTotales / 3600);
  const minutes = Math.floor((segundosTotales % 3600) / 60);
  const seconds = segundosTotales % 60;
  return {
    hours,
    minutes,
    seconds,
  };
}

const isSameDay = (date1, date2) => {
  if (!(date1 instanceof Date) || !(date2 instanceof Date)) return false;
  return (
    date1.getDate() === date2.getDate() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getFullYear() === date2.getFullYear()
  );
};

const getDaysDifference = (startDate, endDate) => {
  if (!isDate(startDate)) startDate = new Date(startDate);
  if (!isDate(endDate)) endDate = new Date(endDate);

  const d1 = moment(startDate).startOf('day');
  const d2 = moment(endDate).startOf('day');

  return Math.abs(d1.diff(d2, 'days'));
}


// Validation duration
const isValidDurationMax8h = (timeStart, timeEnd) => {
  const start = moment(timeStart);
  const end = moment(timeEnd);
  if (!start.isValid() || !end.isValid()) return false;
  if (!start.isBefore(end)) return false;
  const hours = end.diff(start, 'hours', true);
  return hours <= 8;
}


const onAccept = () => {
  dialogValidation8h.value = false
  isAcceptedNomoreThan8.value = true;
  stopTimeEntry();
}

const onCancel = () => {
  dialogValidation8h.value = false
  disabledStopButton.value = false;
}

</script>

<template>
  <div class="row currentEntryCard">
    <!-- COLUMN 1 -->
    <div class="col-7 column1 entry-info justify-content-center">
      <div class="row d-flex" style="margin-top: 3px;">
        <!-- DESCRIPTION -->
        <div class="col-7 el-col-xs-24 tracker-description">
          <el-autocomplete ref="refAutocompleteDescription" v-model="description" :fetch-suggestions="querySearch"
            @change="changeEntryDescription" style="width: 100%;" @select="handleSelect"
            @keydown="moveDescriptionCursorToEdge" placeholder="Descripción de proyecto" class="inputDescription"
            popper-class="custom-autocomplete-popper" :title="description" value-key="description"
            :maxlength="MAX_DESCRIPTION_LENGTH">
            <template #default="{ item }">
              <div class="custom-suggestion d-flex fixed-dropdown-item">
                <span class="ellipsis-span" style="max-width: 40%;" :title="item.description">
                  {{ item.description }}
                </span>
                <span class="ellipsis-span" style="max-width: 20%;" :title="item.project.name">
                  <tracker-project-info :project="item.project" :select="true" class="projectSearch" />
                </span>
                <span class="ellipsis-span tracker-tagD" style="max-width: 40%;" :title="getTagsText(item.tags)">
                  {{ getTagsText(item.tags) }}
                </span>
              </div>
            </template>
          </el-autocomplete>
          <span v-if="showCharCounter" :class="['char-counter', charCounterClass]">
            {{ descriptionLength }}/{{ MAX_DESCRIPTION_LENGTH }}
          </span>
        </div>
        <!-- PROJECT -->
        <div class="col-5 tracker-project current-project el-col-xs-24 projectResponsive">
          <!-- <el-tooltip class="box-item" effect="dark" :content="props.entry.project.name" placement="top"> -->
          <el-dropdown trigger="click" :hide-on-click="false" ref="refDropdownProject" class="addProjectDropdown"
            placement="bottom" @visible-change="handleDropdownProjectVisibleChange">
            <p v-if="!project" @click="dropdownProject = true" style="margin-top: 10px;">
              Añadir Proyecto
            </p>
            <tracker-project-info :project="project" class="el-dropdown-link" @click="dropdownProject = true;"
              v-if="project" :currentEntry="true" />
            <template #dropdown>
              <el-dropdown-menu class="dropProject">
                <el-dropdown-item style="height: 300px;" v-if="dropdownProject">
                  <TrackerProjectSearch @get-project="getNewProject" :project="project"
                    :dropdown-open="isDropdownOpen" />
                </el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
          <!-- </el-tooltip> -->
        </div>
      </div>
    </div>
    <!-- COLUMN 2 -->
    <div class="col-5 column2 entry-info align-items-center el-col-xs-24">
      <div class="row d-flex">
        <!-- ETIQUETAS -->
        <div class="col-6  el-col-xs-24">
          <el-tooltip class="box-item" effect="dark" :content="tagsText" placement="top">
            <el-dropdown trigger="click" :hide-on-click="false" max-height="200" class="tracker-current-tags"
              popper-class="dropdownEntryTags">
              <div class="tracker-tag col-6 align-items-center align-content-center " style="margin-top: 10px;">
                {{ tagsText }}
              </div>
              <template #dropdown>
                <el-dropdown-menu class="dropTags">
                  <el-dropdown-item>
                    <el-select :value-key="'@id'" clearable filterable multiple collapse-tags collapse-tags-tooltip
                      placeholder="Etiqueta" v-model="tags" class="tracker-select" @change="changeTags()">
                      <el-option v-for="tag in optionTags" :key="tag['@id']" :label="tag.name" :value="tag">
                      </el-option>
                    </el-select>
                  </el-dropdown-item>
                </el-dropdown-menu>
              </template>
            </el-dropdown>
          </el-tooltip>

        </div>
        <!-- IMPORTE -->
        <div class="tracker-button col-1 el-col-xs-4" style="margin-left: auto; margin-top: 6px;">
          <el-tooltip class="box-item" effect="dark" :content="props.entry.billable ? 'Facturable' : 'No facturable'"
            placement="top">
            <span class="w-10 tracker-button pointer"
              :class="props.entry.billable ? 'tracker-button--active' : 'tracker-button--inactive'"
              @click="changeBillable">
              €
            </span>
          </el-tooltip>
        </div>
        <div class="col-5 el-col-xs-20">
          <div class="row d-flex">
            <!-- DURACION // HORA DE INICIO -->
            <div class="col-3 text-center p-2 flex-column align-items-center duration-trackernet">
              <el-dropdown trigger="click" :hide-on-click="false" max-height="200" class="temp"
                popper-class="clock-dropdown-popper">
                <div class="row">
                  <div class="col-11">
                    <clock :startTime="totalTime" style="margin-top: 8px;" />
                  </div>
                  <div class="col-1" style="margin-left: -15px; margin-top: 8px;">
                    <sup v-if="daysDiff > 0 && !isNaN(daysDiff)"> +{{ daysDiff }}</sup>
                  </div>
                </div>
                <template #dropdown>
                  <el-dropdown-menu class="dropClock">
                    <el-dropdown-item>
                      <span>Hora de Inicio </span>
                      <el-input ref="refInputStartTime" v-model="inputStartTime" @focus="focusInputHour()"
                        @change="checkChangeInputHour()" @blur="blurInputHour()"
                        @click="refInputStartTime.select()"></el-input>
                      <span> {{ day }} </span>
                    </el-dropdown-item>
                  </el-dropdown-menu>
                </template>
              </el-dropdown>

            </div>
            <!-- DETENER -->
            <div class="col-7 text-center p-2 flex-column justify-content-end stop-button-trackernet">
              <el-tooltip class="box-item" effect="dark" content="Detener (s)" placement="top">
                <el-button type="danger" class="w-10" @click="stopTimeEntry" :disabled="disabledStopButton">
                  Detener
                </el-button>
              </el-tooltip>

            </div>
            <!-- MENU DROPDOWN -->
            <div class="col-1 text-center p-2 flex-column justify-content-end">
              <el-dropdown>
                <span class="el-dropdown-link">
                  <el-icon class="el-icon--right" style="height: 30px;">
                    <more-filled
                      style="transform: rotate(90deg);color:#1D1D1B80; margin-left: -10px; font-size: 20px;" />
                  </el-icon>
                </span>
                <template #dropdown>
                  <el-dropdown-menu>
                    <el-dropdown-item @click="openDialogSplit" :disabled="isDisabledSplit">Dividir</el-dropdown-item>
                    <el-dropdown-item @click="discard" style="color:red;">Descartar</el-dropdown-item>
                  </el-dropdown-menu>
                </template>
              </el-dropdown>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <el-dialog v-model="dialogSplit" title="Dividir tarea" width="600"
    style="height: 250px; overflow: hidden; max-width: 90%;" @close="closeDialogSplit">
    <TrackerSplit v-if="dialogSplit" :entry="props.entry" :timeEntry="{ startTime, endTime }"
      @reload-entries="reloadEntries" @close-dialog="closeDialogSplit" />
  </el-dialog>

  <SharedDeleteConfirmDialog v-model="discardDialogShown" title="Eliminar entrada"
    message="¿Estás seguro de descartar esta entrada?" @confirm="confirmDiscard" />

  <el-dialog v-model="dialogValidation8h" :title="validation8hTitle" :width="dialogWidth" :fullscreen="isMobile"
    :close-on-click-modal="false" align-center destroy-on-close>
    <div class="dialog-body">
      <el-icon color="#E6A23C" class="warn-icon" v-if="!isMobile">
        <WarningFilled />
      </el-icon>
      <div class="dialog-text">
        <p class="headline">La tarea excede las 8 horas</p>
        <p class="subtext">
          Esta acción creará una tarea con una duración máxima de <strong>8 horas</strong>.
          ¿Deseas continuar?
        </p>
      </div>
    </div>

    <template #footer>
      <div class="dialog-footer">
        <el-button @click="onCancel">Cancelar</el-button>
        <el-button type="primary" @click="onAccept">Aceptar</el-button>
      </div>
    </template>


  </el-dialog>
</template>
<style scoped lang="scss">
.tracker-button {
  font-size: 20px;
}

.currentEntryCard {
  margin-bottom: -7px;
  margin-top: -7px;
}

:global(.clock-dropdown-popper .dropClock .el-dropdown-menu__item:not(.is-disabled):focus),
:global(.clock-dropdown-popper .dropClock .el-dropdown-menu__item:not(.is-disabled):hover) {
  color: inherit !important;
  background-color: transparent !important;
  outline: none !important;
}

.dropdownDescription {
  .el-input__wrapper {
    .el-input__inner {
      cursor: text !important;
    }
  }
}

.tracker-tagD {
  background-color: #f7d20e !important;
  color: #474ce6 !important;
  padding-left: 10px;
  padding-right: 10px;
}

.fixed-dropdown-item {
  display: flex;
  align-items: center;
  width: 100%;
  overflow: hidden;
  padding: 3px;

  >span {
    // flex: 1 1 auto;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    margin-right: 8px;
  }
}

:deep(.custom-autocomplete-popper) {
  width: 300px !important;
  max-height: 200px !important;
}

.custom-autocomplete-popper {
  width: 300px !important;
  /* Ajusta el ancho deseado */
  max-height: 200px !important;
  /* Ajusta el alto deseado, o usa height si es fijo */
  /* Puedes agregar otros estilos, como padding, borde, etc. */
}

.tracker-current-tags {
  @media (max-width: 767px) {
    margin-bottom: 10px;
  }
}


.dialog-body {
  display: flex;
  gap: 12px;
  align-items: flex-start;
}

.warn-icon {
  font-size: 28px;
  flex-shrink: 0;
  margin-top: 4px;
}

.dialog-text .headline {
  margin: 0 0 6px 0;
  font-weight: 600;
  font-size: 16px;
}

.dialog-text .subtext {
  margin: 0;
  color: var(--el-text-color-secondary);
  line-height: 1.5;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

/* Ajustes responsive finos */
@media (max-width: 600px) {
  .dialog-body {
    flex-direction: column;
  }
}

.tracker-description {
  position: relative;
}

.char-counter {
  font-size: 11px;
  position: absolute;
  right: 10px;
  top: -18px;
  font-weight: 500;
  transition: color 0.3s ease;
}

.char-counter-info {
  color: #909399;
}

.char-counter-warning {
  color: #E6A23C;
}

.char-counter-limit {
  color: #F56C6C;
  font-weight: 600;
}

.dialog-body {
  display: flex;
  gap: 12px;
  align-items: flex-start;
}

.warn-icon {
  font-size: 28px;
  flex-shrink: 0;
  margin-top: 4px;
}

.dialog-text .headline {
  margin: 0 0 6px 0;
  font-weight: 600;
  font-size: 16px;
}

.dialog-text .subtext {
  margin: 0;
  color: var(--el-text-color-secondary);
  line-height: 1.5;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

@media(max-width: 767px) {

  .projectResponsive {
    border-radius: 20px;
    padding: 0;
    border: 1px solid black !important;
    margin-top: 8px;
    margin-left: 10px;
    max-width: 95%;
  }

  .el-dropdown-link {
    margin-top: 8px;
    margin-bottom: 10px;
    margin-left: 10px;
  }
}

/* Ajustes responsive finos */
@media (max-width: 600px) {
  .dialog-body {
    flex-direction: column;
  }
}

@media (max-width:1366px) {
  .tracker-tag {
    margin-left: 0px !important;
    width: 250px !important;
    max-width: 250px !important;
  }
}

@media (min-width:1366px) {
  .tracker-tag {
    margin-left: -35px;
    width: 250px !important;
    max-width: 250px !important;
  }
}
</style>
